import { useUpdateBrowserSubscription } from "@recare/app/src/apollo/hooks/mutations";
import { GenericMutationType } from "@recare/app/src/apollo/hooks/mutations/utils";
import { useLoggedInAccount } from "@recare/app/src/reduxentities/selectors";
import { activateBrowserNotification } from "@recare/core/model/utils/featureFlags";
import { BrowserSubscription } from "@recare/core/types";
import { useEffect } from "react";
// ====================== UTILS ======================
const encodeB64KeyToUint8ArrayBuffer = (base64String: any) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    // eslint-disable-next-line no-useless-escape
    .replace(/\-/g, "+")
    .replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// ====================== LOGIC ======================

//Register Service Worker
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    console.log("Service Worker isn't supported on this browser.");
    return;
  }
  try {
    const origin = window.location.origin;
    const serviceWorkerRegistration =
      await window.navigator.serviceWorker.getRegistration(origin);
    if (serviceWorkerRegistration) return;
    await window.navigator.serviceWorker.register("/serviceWorker.js");
  } catch (error) {
    console.error("Service worker registration failed:", error);
  }
}

//Get the Service Worker registration and then the subscription info
async function getSubscriptionInfo() {
  if (!("serviceWorker" in navigator)) {
    console.log("Service Worker isn't supported on this browser.");
    return null;
  }
  if (!("PushManager" in window)) {
    console.log("Push messaging isn't supported on this browser.");
    return null;
  }
  try {
    const origin = window.location.origin;
    const serviceWorkerRegistration =
      await window.navigator.serviceWorker.getRegistration(origin);
    if (serviceWorkerRegistration) {
      const subscriptionInfo =
        await serviceWorkerRegistration.pushManager.getSubscription();
      return subscriptionInfo;
    }
    return null;
  } catch (error) {
    console.error("Get SubscriptionInfo failed:", error);
    return null;
  }
}

function timeout(ms: number) {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject("Timed out in " + ms + "ms.");
    }, ms);
  });
}
export async function getSubscriptionInfoWithTimeout() {
  if (!activateBrowserNotification()) {
    console.log("Browser notification is not activated yet");
    return null;
  }

  try {
    const subscriptionInfo = await Promise.race([
      getSubscriptionInfo(),
      timeout(2000),
    ]);
    return subscriptionInfo as unknown as PushSubscription | null | undefined;
  } catch (error) {
    console.error(
      "Get Service Worker Registration and SubscriptionInfo failed:",
      error,
    );
    return null;
  }
}

// Subscribe to push server
async function subscribeUserToPush() {
  try {
    // Public Key must be the same key as backend uses.
    const applicationServerKey = encodeB64KeyToUint8ArrayBuffer(
      "BMJ4MRuhgw9D192Rhk7m-ePSySDzr5P1pnYcrhljm5G1brTqmhUrIushv6mDjBhlrWD-MK497wiHBv8cS8ruMyM",
    );
    const options = { applicationServerKey, userVisibleOnly: true };

    const origin = window.location.origin;
    const serviceWorkerRegistration =
      await window.navigator.serviceWorker.getRegistration(origin);
    let pushSubscription;
    if (serviceWorkerRegistration) {
      pushSubscription = await serviceWorkerRegistration.pushManager.subscribe(
        options,
      );
    }
    return pushSubscription ? pushSubscription.toJSON() : null;
  } catch (error) {
    console.error("Error subscribing to push server:", error);
    return null;
  }
}

async function askPermission(
  updateBrowserSubscription: GenericMutationType<BrowserSubscription>,
) {
  if (!("Notification" in window)) {
    console.log("Browser Notification isn't supported on this browser. ");
    return;
  }
  if (!("showNotification" in ServiceWorkerRegistration.prototype)) {
    console.log("Browser Notification isn't supported in the service worker.");
    return;
  }

  if (!("PushManager" in window)) {
    console.log("Push messaging isn't supported on this browser.");
    return;
  }

  if (Notification.permission === "denied") {
    console.log("The user has blocked notifications.");
    return;
  }

  if (!activateBrowserNotification()) {
    console.log("Browser notification is not activated yet");
    return;
  }

  const subscription = await getSubscriptionInfoWithTimeout();

  try {
    if (
      Notification.permission === "default" ||
      (Notification.permission === "granted" && !subscription)
    ) {
      const permissionResult = await Notification.requestPermission();

      if (permissionResult === "granted") {
        await registerServiceWorker();
        const subscriptionInfo = await subscribeUserToPush();
        if (subscriptionInfo) {
          const id_token = localStorage.getItem("id_token");
          updateBrowserSubscription({ ...subscriptionInfo, id_token });
        }
      }
    }
  } catch (error) {
    console.error(
      "Browser notification - set the initial state failed:",
      error,
    );
  }
}

export default function useAskPermission(id?: number | null) {
  const account = useLoggedInAccount();
  const [updateBrowserSubscription] = useUpdateBrowserSubscription({
    id: account?.id ?? -1,
  });

  useEffect(() => {
    askPermission(updateBrowserSubscription);
  }, [id]);
}
